
import { useEffect, useRef, lazy, Suspense } from "react";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { setupCookieDebugUtils, setupDebugKeyboardShortcuts } from "./cookie/cookieDebugUtils";

// Use lazy loading for the CookieConsentContent to improve initial load performance
const CookieConsentContent = lazy(() => import('./cookie/CookieConsentContent').then(module => ({
  default: module.CookieConsentContent
})));

const CookieBanner = () => {
  const {
    isVisible,
    setIsVisible,
    acceptAllCookies,
    acceptNecessaryCookies,
    status
  } = useCookieConsent();
  
  // Add to Window for debugging
  useEffect(() => {
    // Only setup debug tools in development
    if (process.env.NODE_ENV === 'development') {
      // Setup debug keyboard shortcuts
      const cleanupKeyboardShortcuts = setupDebugKeyboardShortcuts(setIsVisible);
      
      // Add debug utilities to window
      (window as any).cookieBannerDebug = setupCookieDebugUtils(setIsVisible, status);
      
      return () => {
        cleanupKeyboardShortcuts();
        delete (window as any).cookieBannerDebug;
      };
    }
    
    return () => {};
  }, [isVisible, setIsVisible, status]);

  // Don't render anything if banner shouldn't be visible or when SSR
  if (typeof window === 'undefined' || !isVisible) return null;

  return (
    <Suspense fallback={null}>
      <CookieConsentContent
        onAcceptAll={acceptAllCookies}
        onAcceptNecessary={acceptNecessaryCookies}
        onClose={() => {
          setIsVisible(false);
          document.body.style.overflow = "auto";
        }}
      />
    </Suspense>
  );
};

export default CookieBanner;
